import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ticketApi } from '../../api/ticket.api';
import { aiManagementApi } from '../../api/aiManagement.api';
import { BASE_MEDIA_URL } from '../../api/client';
import { Spinner, Skeleton, Badge, Btn, DraftTextarea, TicketAiSubmitOverlay } from '../../components/ui/index';
import { TICKET_AI_SUBMIT_MESSAGE } from '../../utils/ticketTitle';
import { useUI } from '../../store/UIContext';
import { useAuth } from '../../store/AuthContext';
import { timeAgo, parseBackendDateTime } from '../../utils/persianNumber';
import { saveDraft, loadDraft, clearDraft, clearTicketFollowUpDrafts } from '../../utils/draftStorage';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import AIAssistantPanel from '../../components/ai/AIAssistantPanel';
import CustomerTicketProgress from '../../components/tickets/CustomerTicketProgress';
import { ThumbUpIcon, ThumbDownIcon, FeedbackSuccessLogo } from '../../components/feedback/FeedbackMoodPicker';
import ResponseRating from '../../components/tickets/ResponseRating';

const pick = (d) => (Array.isArray(d) ? d[0] || null : d || null);
const isFilled = (v) => v != null && String(v).trim() !== '';

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToast } = useUI();
  const { user } = useAuth() || {};
  const [ticket, setTicket] = useState(null);
  const [response, setResponse] = useState(null);
  const [thread, setThread] = useState([]);
  const [files, setFiles] = useState([]);
  const [responseFilesByTicket, setResponseFilesByTicket] = useState({});
  const [chatHistory, setChatHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deciding, setDeciding] = useState(false);
  // Persisted (not just in-memory) so refreshing or navigating away and
  // back doesn't forget the customer already chose "ادامه می‌دهم" and
  // force them to click it again - see startContinue/sendFollowUp.
  const [continuing, setContinuing] = useState(() => loadDraft(`ticket_continuing_${id}`) === '1');
  const [followUpText, setFollowUpText] = useState('');
  const [clearFollowUpSignal, setClearFollowUpSignal] = useState(0);
  const [sendingFollowUp, setSendingFollowUp] = useState(false);
  const [submitOverlay, setSubmitOverlay] = useState(false);
  const [ratingDone, setRatingDone] = useState({});
  // get_ticket_simple_info 404s once this ticket_id is superseded by a
  // newer follow-up in the same thread (see getTicketSimpleInfo) - either
  // side sending a follow-up message used to leave whoever had the OLD
  // ticket_id open just silently stuck with stale content forever, since
  // nothing ever pointed them at the ticket the conversation moved to.
  const [ticketNotFound, setTicketNotFound] = useState(false);
  const activeTicketRef = useRef(id);

  useEffect(() => {
    activeTicketRef.current = id;
    setChatHistory([]);
  }, [id]);

  const fetchData = useCallback(async (showSpinner = true) => {
    const ticketId = id;
    if (showSpinner) setLoading(true);
    if (showSpinner) setError('');
    try {
      const [tRes, rRes, cRes, wRes] = await Promise.allSettled([
        ticketApi.getTicketSimpleInfo(ticketId),
        ticketApi.getResponseSimpleInfo(ticketId),
        ticketApi.getTicketChatHistory(ticketId),
        // Walks the thread straight off the `tickets` table (see
        // queryGetTicketWithResponse) so it still resolves even once this
        // ticket_id itself is superseded and the two calls above 404 -
        // that's what lets the redirect effect below find where to send
        // the customer instead of leaving them on a dead ticket_id.
        ticketApi.getTicketWithResponse(ticketId),
      ]);
      if (ticketId !== activeTicketRef.current) return;
      if (tRes.status === 'fulfilled') {
        setTicket(pick(tRes.value.data));
        setTicketNotFound(false);
      } else {
        setTicketNotFound(true);
      }
      if (rRes.status === 'fulfilled') setResponse(pick(rRes.value.data));
      setChatHistory(Array.isArray(cRes.value?.data?.messages) ? cRes.value.data.messages : []);
      // full back-and-forth across the whole reply chain (ticket -> response
      // -> follow-up ticket -> response -> ...), grouped by root_ticket_id
      // server-side (see queryGetTicketWithResponse), not just this ticket's
      // immediate parent/children.
      const threadRows = wRes.status === 'fulfilled' && Array.isArray(wRes.value.data) ? wRes.value.data : [];
      setThread(threadRows);
      if (tRes.status === 'fulfilled') ticketApi.setUserSeenMessage(ticketId).catch(() => {});
      // Collect every ticket id in the conversation chain - response
      // attachments live on the ticket that was answered, so if the
      // customer opened a follow-up ticket the previous answer's files
      // would be invisible if we only asked for this page's id.
      const ticketIds = [...new Set(
        [ticketId, ...threadRows.map((r) => r.ticket_id)].filter((x) => x != null).map(String)
      )];
      Promise.allSettled([
        ticketApi.getFileList(ticketId, 'ticket'),
        ...ticketIds.map((tid) => ticketApi.getFileList(tid, 'response')),
      ]).then(([tFiles, ...rFiles]) => {
        if (ticketId !== activeTicketRef.current) return;
        setFiles(tFiles.status === 'fulfilled' && Array.isArray(tFiles.value.data) ? tFiles.value.data : []);
        const byTicket = {};
        ticketIds.forEach((tid, i) => {
          const res = rFiles[i];
          byTicket[tid] = res?.status === 'fulfilled' && Array.isArray(res.value.data) ? res.value.data : [];
        });
        setResponseFilesByTicket(byTicket);
      });
    } catch {
      // The two calls that can 404 (ticket superseded by a newer follow-up)
      // are now handled per-call above via allSettled/ticketNotFound - this
      // only catches a genuine unexpected failure, and even then a
      // background poll (showSpinner=false) must not blow away whatever
      // the customer is currently looking at with a full-page error block.
      if (showSpinner) setError('خطا در دریافت اطلاعات تیکت. لطفاً دوباره تلاش کنید.');
    }
    if (ticketId === activeTicketRef.current) setLoading(false);
  }, [id]);

  useEffect(() => { fetchData(); }, [fetchData]);
  // Paused while the customer/employee has an in-progress follow-up draft
  // or a decision in flight, so a background refresh can't wipe it out.
  useAutoRefresh(() => fetchData(false), { intervalMs: 2000, paused: sendingFollowUp || deciding });

  // Jump straight to whichever ticket is now current in the same thread
  // once this one is superseded (see ticketNotFound above) - the employee
  // sending a follow-up message on their side used to leave a customer
  // sitting on this page stuck on stale content with no way back into the
  // conversation until they reopened it from their ticket list themselves.
  useEffect(() => {
    if (!ticketNotFound || thread.length === 0) return;
    const latestTicketId = [...thread].reverse().find((r) => r.ticket_id)?.ticket_id;
    if (latestTicketId && String(latestTicketId) !== String(id)) {
      navigate(`/tickets/${latestTicketId}`, { replace: true });
    }
  }, [ticketNotFound, thread, id, navigate]);

  const hasResponse = response && (isFilled(response.title) || isFilled(response.description));
  const latestResponseId = response?.id || [...thread].reverse().find((r) => r.response_id)?.response_id;
  const latestRated = !!(latestResponseId && ratingDone[latestResponseId]);

  const lastResponseIdx = thread.reduce((acc, r, i) => (r.response_id && isFilled(r.response_text) ? i : acc), -1);
  const markRated = (responseId, done) => {
    setRatingDone((prev) => (prev[responseId] === done ? prev : { ...prev, [responseId]: done }));
  };

  // ticket_is_active is the same flag activate_ticket/deactivate_ticket flip
  // (see queryChangeTicketActivation) - null on first load until the user
  // decides, then reflects their choice on refresh.
  const decided = ticket?.is_active === 0 || continuing;

  // No explicit "closed at" timestamp is returned for this page - the last
  // response in the thread is the closest available proxy for when the
  // ticket was closed. Unknown date defaults to "within a week" (the
  // reopen option) rather than silently blocking it.
  const lastResponseDate = [...thread].reverse().find((r) => r.response_date)?.response_date;
  const daysSinceClosed = lastResponseDate ? (Date.now() - parseBackendDateTime(lastResponseDate)) / 86400000 : null;
  const closedWithinWeek = daysSinceClosed === null || isNaN(daysSinceClosed) || daysSinceClosed <= 7;

  // Confirm/continue doubles as the customer satisfaction signal (CSAT):
  // "بله تایید می‌کنم" -> satisfied, "خیر، ادامه می‌دهم" -> not satisfied.
  // Best-effort - a feedback-write failure shouldn't block closing/reopening
  // the ticket itself, so it's fire-and-forget with its own catch.
  const submitFeedback = (satisfaction) => {
    if (response?.id == null) return;
    aiManagementApi.submitResponseFeedback({ ticket_id: id, response_id: response.id, satisfaction }).catch(() => {});
  };

  const confirmResponse = async () => {
    setDeciding(true);
    try {
      await ticketApi.deactivateTicket(id);
      submitFeedback('satisfied');
      clearDraft(`ticket_continuing_${id}`);
      addToast('از پاسخ شما متشکریم', 'success');
      await fetchData();
    } catch { addToast('خطا در ثبت تایید', 'error'); }
    setDeciding(false);
  };

  const startContinue = async () => {
    setDeciding(true);
    try {
      await ticketApi.activateTicket(id);
      submitFeedback('not_satisfied');
      saveDraft(`ticket_continuing_${id}`, '1');
      setContinuing(true);
    } catch { addToast('خطا در بازگشایی تیکت', 'error'); }
    setDeciding(false);
  };

  // For a ticket closed more than a week ago: don't reopen the old ticket
  // (startContinue's activateTicket) - just reveal the same reply box, and
  // sendFollowUp still creates a fresh linked ticket (main_ticket_id) same
  // as always. Only difference from startContinue is skipping the reopen.
  const startReply = () => {
    saveDraft(`ticket_continuing_${id}`, '1');
    setContinuing(true);
  };

  const sendFollowUp = async () => {
    if (!followUpText.trim()) { addToast('متن پیام الزامی است', 'error'); return; }
    setSendingFollowUp(true);
    setSubmitOverlay(true);
    try {
      // Follow-ups reuse the conversation's existing summary/title; the
      // server copies it from the parent ticket, so no new AI summary call.
      const res = await ticketApi.sendTicket({
        description: followUpText,
        category: 'سایر',
        main_ticket_id: Number(id),
        priority: ticket?.priority || 0,
      });
      addToast('پیام شما ارسال شد', 'success');
      setFollowUpText('');
      setContinuing(false);
      await clearTicketFollowUpDrafts(id, user?.username);
      setClearFollowUpSignal((c) => c + 1);
      // Navigate to the new follow-up ticket's own page so the URL/id stays
      // in sync with "the ticket currently awaiting a response" - the thread
      // itself (fetched by root_ticket_id) would show the same full chain
      // either way.
      const newTicketId = res?.data?.ticket_id;
      if (newTicketId) navigate(`/tickets/${newTicketId}`);
      else await fetchData();
    } catch { addToast('خطا در ارسال پیام', 'error'); }
    setSendingFollowUp(false);
    setSubmitOverlay(false);
  };

  return (
    <div className="fade-in">
      <TicketAiSubmitOverlay open={submitOverlay} message={TICKET_AI_SUBMIT_MESSAGE} />
      <button onClick={() => navigate(-1)} style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 13, marginBottom: 20, padding: 0 }}>
        ← بازگشت به فهرست
      </button>

      {loading
        ? <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 24 }}><Skeleton h={22} w="60%" style={{ marginBottom: 12 }} /><Skeleton h={14} /><Skeleton h={14} w="80%" /></div>
          </div>
        : error
          ? <div style={{ textAlign: 'center', padding: 60, color: '#ef4444' }}>{error}</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 760 }}>
              {/* Ticket card */}
              {ticket && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(59,130,246,0.1)', boxShadow: '0 4px 16px rgba(59,130,246,0.08)', overflow: 'hidden' }}>
                  <div className="pj-row-card" style={{ padding: '16px 24px', background: 'linear-gradient(135deg,rgba(59,130,246,0.06),rgba(37,99,235,0.02))', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 36, height: 36, borderRadius: 10, background: '#eff6ff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>📋</div>
                      <div>
                        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', margin: 0 }}>{ticket.title || 'تیکت'}</h2>
                        {ticket.send_date && <span style={{ fontSize: 11, color: '#94a3b8' }}>{timeAgo(ticket.send_date)}</span>}
                      </div>
                    </div>
                    {ticket.is_active === 0 && (
                      <Badge variant="gray">بسته شده</Badge>
                    )}
                  </div>
                  <div style={{ padding: '12px 24px', borderBottom: '1px solid #f1f5f9', background: '#fafbfc' }}>
                    <CustomerTicketProgress ticket={{ ...ticket, response_id: response?.id, response_title: response?.title, description: response?.description, responding: hasResponse && !ticket?.responsed }} />
                  </div>
                  <div style={{ padding: 24 }}>
                    <p style={{ fontSize: 14, color: '#334155', lineHeight: 1.8, margin: 0, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>
                    {ticket.priority ? (
                      <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                        <Badge variant="orange">اولویت: {ticket.priority}</Badge>
                      </div>
                    ) : null}
                    {files.length > 0 && (
                      <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 600, color: '#64748b' }}>پیوست‌ها</span>
                        {files.map((f, i) => (
                          <a key={i} href={BASE_MEDIA_URL + f.file_name} target="_blank" rel="noreferrer"
                            style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f8faff', borderRadius: 8, border: '1px solid #e2e8f0', color: '#2563eb', fontSize: 12, textDecoration: 'none' }}>
                            📄 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Full interactive process: every round of (user message ->
                  employee response) across this ticket and any follow-up
                  tickets linked to it via main_ticket_id, in order. */}
              {thread.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(59,130,246,0.1)', padding: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>روند گفتگو</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {/* only the latest response can be rated; earlier ratings stay stored but are hidden */}
                    {thread.map((round, i) => (
                      <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {!!(round.ticket_text && String(round.ticket_text).trim()) && (
                        <div style={{ alignSelf: 'flex-start', maxWidth: '85%', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: '10px 14px' }}>
                          <div style={{ fontSize: 10, color: '#3b82f6', fontWeight: 700, marginBottom: 4 }}>👤 شما · {timeAgo(round.send_date)}</div>
                          <p style={{ fontSize: 13, color: '#334155', margin: 0, whiteSpace: 'pre-wrap' }}>{round.ticket_text}</p>
                        </div>
                        )}
                        {isFilled(round.response_text) && (
                          <div style={{ alignSelf: 'flex-end', maxWidth: '85%', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 14px' }}>
                            <div style={{ fontSize: 10, color: '#15803d', fontWeight: 700, marginBottom: 4 }}>🎧 پشتیبانی{round.response_date ? ` · ${timeAgo(round.response_date)}` : ''}</div>
                            <p style={{ fontSize: 13, color: '#334155', margin: 0, whiteSpace: 'pre-wrap' }}>{round.response_text}</p>
                            {(responseFilesByTicket[String(round.ticket_id ?? id)] || []).length > 0 && (
                              <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                {(responseFilesByTicket[String(round.ticket_id ?? id)] || []).map((f, fi) => (
                                  <a key={fi} href={BASE_MEDIA_URL + f.file_name} target="_blank" rel="noreferrer"
                                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: '#15803d', textDecoration: 'none' }}>
                                    📎 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                                  </a>
                                ))}
                              </div>
                            )}
                            {round.response_id && i === lastResponseIdx ? (
                              <div style={{ marginTop: 10 }}>
                                <ResponseRating responseId={round.response_id} onHasRating={markRated} />
                              </div>
                            ) : null}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Fallback: response files for this ticket when thread is empty */}
              {hasResponse && (responseFilesByTicket[String(id)] || []).length > 0 && thread.length === 0 && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #bbf7d0', padding: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: '#15803d', marginBottom: 10 }}>📎 پیوست‌های پاسخ پشتیبانی</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {(responseFilesByTicket[String(id)] || []).map((f, i) => (
                      <a key={i} href={BASE_MEDIA_URL + f.file_name} target="_blank" rel="noreferrer"
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0', color: '#15803d', fontSize: 12, textDecoration: 'none' }}>
                        📄 <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.file_name}</span>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {hasResponse && thread.length === 0 && response?.id && (
                <ResponseRating responseId={response.id} onHasRating={markRated} />
              )}

              {/* Confirm / continue - after seeing the response, ask the
                  user whether it resolved their issue. Yes closes the
                  ticket (deactivate_ticket); no reopens it and lets them
                  send a follow-up message linked to this ticket
                  (send_ticket with main_ticket_id). */}
              {hasResponse && !decided && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(59,130,246,0.1)', padding: 20, display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div>
                    <span style={{ fontSize: 13, color: '#334155', fontWeight: 600 }}>آیا این پاسخ مشکل شما را برطرف کرد؟</span>
                    {!latestRated && (
                      <p style={{ fontSize: 12, color: '#64748b', margin: '6px 0 0' }}>امتیاز دادن به پاسخ اختیاری است.</p>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <Btn variant="success" onClick={confirmResponse} loading={deciding} style={{ minHeight: 44, gap: 8 }}>
                      <ThumbUpIcon /> بله، تایید می‌کنم
                    </Btn>
                    <Btn variant="secondary" onClick={startContinue} loading={deciding} style={{ minHeight: 44, gap: 8 }}>
                      <ThumbDownIcon /> خیر، ادامه می‌دهم
                    </Btn>
                  </div>
                </div>
              )}

              {ticket?.is_active === 0 && !continuing && (
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '16px 20px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 12, color: '#166534', fontSize: 13 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                    <FeedbackSuccessLogo size={36} />
                    این تیکت توسط شما تایید و بسته شده است.
                  </span>
                  {closedWithinWeek ? (
                    <Btn size="sm" variant="secondary" onClick={startContinue} loading={deciding}>ریپلای</Btn>
                  ) : (
                    <Btn size="sm" variant="secondary" onClick={startReply}>پاسخ به تیکت</Btn>
                  )}
                </div>
              )}

              {continuing && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(59,130,246,0.1)', padding: 24 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>ادامه گفتگو</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <DraftTextarea draftKey={`ticket_followup_${id}`} rows={4} placeholder="توضیح دهید مشکل شما همچنان ادامه دارد یا چه چیزی نیاز است..." onChange={setFollowUpText} clearTrigger={clearFollowUpSignal} onSubmit={() => { if (!sendingFollowUp) sendFollowUp(); }} />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                      <Btn onClick={sendFollowUp} loading={sendingFollowUp}>📤 ارسال</Btn>
                    </div>
                  </div>
                </div>
              )}

              {/* Full AI chat transcript - only present for tickets resolved
                  via the customer's AI chat (see queryLinkAIChatTicket) */}
              {chatHistory.length > 0 && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(139,92,246,0.15)', boxShadow: '0 4px 16px rgba(139,92,246,0.06)', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 24px', background: 'linear-gradient(135deg,rgba(139,92,246,0.06),rgba(109,40,217,0.02))', borderBottom: '1px solid rgba(139,92,246,0.12)', fontSize: 13, fontWeight: 700, color: '#6d28d9' }}>
                    🤖 گفتگوی کامل با دستیار هوش‌مصنوعی
                  </div>
                  <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto' }}>
                    {chatHistory.map((m, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-start' : 'flex-end' }}>
                        <div style={{
                          maxWidth: '85%', padding: '8px 12px', borderRadius: 10, fontSize: 13, whiteSpace: 'pre-wrap',
                          background: m.role === 'user' ? '#eff6ff' : '#faf5ff',
                          color: '#334155',
                          border: m.role === 'user' ? '1px solid #bfdbfe' : '1px solid #e9d5ff',
                        }}>
                          {m.content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!hasResponse && ticket && (
                <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: '16px 20px', color: '#b45309', fontSize: 13 }}>
                  ⏳ تیکت شما در صف بررسی است. به محض ارسال پاسخ مطلع خواهید شد.
                </div>
              )}

              {/* Ticket still waiting on a first response - let the user add
                  more info/attachments as a follow-up ticket in the same
                  chain instead of forcing them to wait for a reply first. */}
              {!hasResponse && ticket?.is_active !== 0 && (
                <div style={{ background: '#fff', borderRadius: 16, border: '1px solid rgba(59,130,246,0.1)', padding: 24 }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', margin: '0 0 14px' }}>افزودن توضیحات بیشتر</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <DraftTextarea draftKey={`ticket_followup_${id}`} rows={4} placeholder="اطلاعات یا توضیحات بیشتری که لازم است اضافه کنید..." onChange={setFollowUpText} clearTrigger={clearFollowUpSignal} onSubmit={() => { if (!sendingFollowUp) sendFollowUp(); }} />
                    <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                      <Btn onClick={sendFollowUp} loading={sendingFollowUp}>📤 ارسال</Btn>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
      }
    </div>
  );
}

